import React from 'react';
import {
  PROPERTY_TYPES,
  PROPERTY_TYPE_LABELS,
  type FilterState,
  type PropertyType,
} from '../constants';

interface FilterPanelProps {
  filters: FilterState;
  onChange: (next: FilterState) => void;
}

export default function FilterPanel({ filters, onChange }: FilterPanelProps) {
  const update = (patch: Partial<FilterState>) => onChange({ ...filters, ...patch });

  const toggleType = (t: PropertyType) => {
    const next = filters.property_types.includes(t)
      ? filters.property_types.filter((x) => x !== t)
      : [...filters.property_types, t];
    update({ property_types: next });
  };

  return (
    <div className="space-y-6 text-sm">
      <Slider
        label="Min ownership duration"
        unit="years"
        value={filters.min_ownership ?? 0}
        min={0}
        max={30}
        onChange={(v) => update({ min_ownership: v })}
      />

      <Slider
        label="Min EPC age"
        unit="years"
        value={filters.min_epc_age ?? 0}
        min={0}
        max={20}
        onChange={(v) => update({ min_epc_age: v })}
      />

      <Slider
        label="Min move-likelihood score"
        unit=""
        value={filters.min_score ?? 0}
        min={0}
        max={100}
        onChange={(v) => update({ min_score: v })}
      />

      <div>
        <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
          Property type
        </label>
        <div className="grid grid-cols-2 gap-1.5">
          {PROPERTY_TYPES.map((t) => {
            const active = filters.property_types.includes(t);
            return (
              <button
                key={t}
                onClick={() => toggleType(t)}
                className={`px-2 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                  active
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
              >
                {PROPERTY_TYPE_LABELS[t]}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
          Last sale price band
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            placeholder="Min £"
            value={filters.min_price ?? ''}
            onChange={(e) =>
              update({ min_price: e.target.value ? Number(e.target.value) : undefined })
            }
            className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-xs focus:outline-none focus:border-emerald-400"
          />
          <span className="text-slate-400">—</span>
          <input
            type="number"
            placeholder="Max £"
            value={filters.max_price ?? ''}
            onChange={(e) =>
              update({ max_price: e.target.value ? Number(e.target.value) : undefined })
            }
            className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-xs focus:outline-none focus:border-emerald-400"
          />
        </div>
      </div>
    </div>
  );
}

interface SliderProps {
  label: string;
  unit: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}

function Slider({ label, unit, value, min, max, onChange }: SliderProps) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {label}
        </label>
        <span className="text-sm font-semibold text-slate-900">
          {value} {unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-emerald-600"
      />
    </div>
  );
}
